import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import {
  APPROVE_REASON,
  DECLINE_REASONS,
  EDIT_REASONS,
  GuaranteeStatus,
  Prescription,
  PrescriptionCategory,
  PrescriptionCreateRequest,
  PrescriptionTypes,
  PrescriptionUpdateRequest,
  PrescriptionsService,
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_LENGTH,
  SERVICE_SEARCH_PAGE_SIZE,
  ServiceMetadata,
  achiCodeToServiceDictionary,
  buildDictionarySearchPayload,
  combineReasons,
  comparePrescriptionCategories,
  laboratoryServiceToServiceDictionary,
  parseReason,
  reasonNotValid,
  toPrescriptionCategories,
} from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, catchError, debounceTime, filter, map, of, switchMap, take } from 'rxjs';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { ServiceDictionary } from '@lib/core/models/protocol.interface';

import { DatepickerComponent } from '@lib/shared-ui/commons/datepicker/datepicker.component';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

export interface FreeFormModalData {
  prescription?: Prescription | null;
  memberId: string;
  guaranteeApplicationId: string | null;
  mode: 'managed' | 'create-request';
  prescriptionType?: PrescriptionTypes;
  isFreeForm?: boolean;
}

const SERVICE_SEARCH_TYPES: PrescriptionTypes[] = [
  PrescriptionTypes.Appointment,
  PrescriptionTypes.RelatedSpecialists,
  PrescriptionTypes.InstrumentalDiagnostic,
  PrescriptionTypes.LaboratoryService,
];

interface FreeFormControls {
  isFreeForm: FormControl<boolean>;
  type: FormControl<PrescriptionTypes>;
  name: FormControl<string>;
  code: FormControl<string | null>;
  quantity: FormControl<number | null>;
  guaranteeStatus: FormControl<GuaranteeStatus | null>;
  startDate: FormControl<Date>;
  endDate: FormControl<Date>;
  franchise: FormControl<number>;
  categories: FormControl<PrescriptionCategory[]>;
  statusReason: FormControl<string | null>;
  considerPaid: FormControl<boolean>;
  reservedPrice: FormControl<string | null>;
}

type FreeFormValue = ReturnType<FormGroup<FreeFormControls>['getRawValue']>;

@Component({
  selector: 'prescriptions-free-form-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInput,
    MatSelectModule,
    MatAutocompleteModule,
    DatepickerComponent,
    MatCheckboxModule,
    MatButtonModule,
    MatIcon,
    LoaderComponent,
    TranslatePipe,
  ],
  templateUrl: './free-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreeFormModalComponent implements OnInit {
  protected readonly data: FreeFormModalData = inject(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<FreeFormModalComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly dictionariesApiService = inject(DictionariesApiService);

  protected readonly prescriptionTypes = [...new Set(Object.values(PrescriptionTypes))];
  protected readonly categoryOptions = signal<PrescriptionCategory[]>([]);
  protected readonly guaranteeStatusOptions: GuaranteeStatus[] = [GuaranteeStatus.Guaranteed, GuaranteeStatus.NotGuaranteed, GuaranteeStatus.Auto];
  protected readonly guaranteeStatus = GuaranteeStatus;

  protected readonly loading = signal<boolean>(false);
  protected readonly isEdit = signal<boolean>(false);
  protected readonly statusChanged = signal<boolean>(false);

  protected readonly freeForm = signal<boolean>(true);
  protected readonly selectedType = signal<PrescriptionTypes>(PrescriptionTypes.Appointment);
  protected readonly servicesOptions = signal<ServiceDictionary[]>([]);
  protected readonly filteredDeclineReasons = signal<string[]>(DECLINE_REASONS);
  protected readonly filteredEditReasons = signal<string[]>(EDIT_REASONS);
  protected readonly isServiceType = computed<boolean>(() => SERVICE_SEARCH_TYPES.includes(this.selectedType()));
  protected readonly nameSearchEnabled = computed<boolean>(() => !this.freeForm() && this.isServiceType());

  protected form!: FormGroup<FreeFormControls>;

  ngOnInit(): void {
    this.isEdit.set(!!this.data.prescription?.id);
    this.initForm();
    this.watchFreeFormToggle();
    this.watchTypeChanges();
    this.watchNameSearch();
    this.watchDeclineReasons();
    this.watchGuaranteeStatus();
    this.initStatusReason();
    this.loadCategories();
  }

  private initStatusReason(): void {
    if (this.data.prescription) {
      this.statusChanged.set(false);
      return;
    }

    this.statusChanged.set(true);
    const statusReason = this.form.controls.statusReason;
    const validators = this.form.controls.guaranteeStatus.value === GuaranteeStatus.Guaranteed ? [Validators.required] : [Validators.required, reasonNotValid(DECLINE_REASONS)];
    statusReason.setValidators(validators);
    statusReason.updateValueAndValidity({ emitEvent: false });
  }

  private initForm(): void {
    const start = new Date();
    if (this.data.isFreeForm ?? true) start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 14);

    this.form = this.fb.group<FreeFormControls>({
      isFreeForm: this.fb.nonNullable.control<boolean>(this.data.isFreeForm ?? true),
      type: this.fb.nonNullable.control<PrescriptionTypes>(this.data.prescriptionType ?? PrescriptionTypes.Appointment, [Validators.required]),
      name: this.fb.nonNullable.control<string>('', [Validators.required]),
      code: this.fb.control<string | null>(null),
      quantity: this.fb.control<number | null>(1, [Validators.min(1)]),
      guaranteeStatus: this.fb.control<GuaranteeStatus | null>(null, [Validators.required]),
      startDate: this.fb.nonNullable.control<Date>(start, [Validators.required]),
      endDate: this.fb.nonNullable.control<Date>(end, [Validators.required]),
      franchise: this.fb.nonNullable.control<number>(0, [Validators.min(0), Validators.max(100)]),
      categories: this.fb.nonNullable.control<PrescriptionCategory[]>([]),
      statusReason: this.fb.control<string | null>(null),
      considerPaid: this.fb.nonNullable.control<boolean>(false),
      reservedPrice: this.fb.control<string | null>(null),
    });

    if (this.data.prescription) this.patchFromPrescription(this.data.prescription);
    this.selectedType.set(this.form.controls.type.value);
    this.freeForm.set(this.form.controls.isFreeForm.value);
  }

  private patchFromPrescription(p: Prescription): void {
    const metadata = p.metadata as ServiceMetadata;
    this.form.patchValue({
      isFreeForm: p.isFreeForm ?? false,
      type: p.type,
      name: p.name,
      code: metadata?.code ?? null,
      quantity: metadata?.quantity != null ? +metadata.quantity : 1,
      guaranteeStatus: (p.guaranteeStatus as GuaranteeStatus) ?? null,
      startDate: p.startDate ? new Date(p.startDate) : new Date(),
      endDate: p.endDate ? new Date(p.endDate) : new Date(),
      franchise: p.franchise ?? 0,
      categories: p.categories ?? [],
      statusReason: p.reason ? parseReason(p.reason).statusReason : null,
      considerPaid: p.considerPaid ?? false,
      reservedPrice: p.reservedPrice ?? null,
    });

    this.categoryOptions.set(p.categories ?? []);
  }

  private watchFreeFormToggle(): void {
    this.form.controls.isFreeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.freeForm.set(value);
      this.servicesOptions.set([]);
    });
  }

  private watchTypeChanges(): void {
    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(type => {
      this.selectedType.set(type);
      this.servicesOptions.set([]);
    });
  }

  private watchNameSearch(): void {
    this.form.controls.name.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        filter((term): term is string => typeof term === 'string' && term.trim().length >= SEARCH_MIN_LENGTH && this.nameSearchEnabled()),
        switchMap(term =>
          (this.selectedType() === PrescriptionTypes.LaboratoryService ? this.searchLaboratoryServices(term) : this.searchAchiCodes(term)).pipe(
            catchError(() => of([] as ServiceDictionary[]))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(services => this.servicesOptions.set(services ?? []));
  }

  private searchLaboratoryServices(searchString: string): Observable<ServiceDictionary[]> {
    return this.dictionariesApiService
      .searchLaboratoryServices(buildDictionarySearchPayload(searchString, SERVICE_SEARCH_PAGE_SIZE))
      .pipe(map(page => (page.items ?? []).map(laboratoryServiceToServiceDictionary)));
  }

  private searchAchiCodes(searchString: string): Observable<ServiceDictionary[]> {
    return this.dictionariesApiService
      .searchAchiCodes(buildDictionarySearchPayload(searchString, SERVICE_SEARCH_PAGE_SIZE))
      .pipe(map(page => (page.items ?? []).map(achiCodeToServiceDictionary)));
  }

  private watchDeclineReasons(): void {
    this.form.controls.statusReason.valueChanges
      .pipe(
        map(value => (value ?? '').toLowerCase()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.filteredDeclineReasons.set(DECLINE_REASONS.filter(reason => reason.toLowerCase().includes(value)));
        this.filteredEditReasons.set(EDIT_REASONS.filter(reason => reason.toLowerCase().includes(value)));
      });
  }

  private watchGuaranteeStatus(): void {
    this.form.controls.guaranteeStatus.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const statusReason = this.form.controls.statusReason;

      if (value === GuaranteeStatus.Guaranteed) {
        statusReason.setValue(APPROVE_REASON);
        statusReason.setValidators([Validators.required]);
      } else {
        statusReason.setValue(null);
        statusReason.setValidators([Validators.required, reasonNotValid(DECLINE_REASONS)]);
      }

      if (this.data.prescription) {
        const original = (this.data.prescription.guaranteeStatus as GuaranteeStatus) ?? null;
        if (value !== original) {
          this.statusChanged.set(true);
        } else {
          this.statusChanged.set(false);
          statusReason.clearValidators();
        }
      }

      statusReason.updateValueAndValidity();
    });
  }

  protected selectServiceOption(option: ServiceDictionary): void {
    this.form.patchValue(
      {
        name: option.nameUa,
        code: option.code,
        quantity: this.form.controls.quantity.value ?? 1,
      },
      { emitEvent: false }
    );
    this.servicesOptions.set([]);
  }

  protected clearStatusReason(): void {
    this.form.controls.statusReason.setValue(null);
  }

  protected loadCategories(): void {
    this.dictionariesApiService
      .getPrescriptionCategories()
      .pipe(toPrescriptionCategories())
      .subscribe(categories => this.categoryOptions.set(categories));
  }

  protected readonly compareCategories = comparePrescriptionCategories;

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const request = this.buildRequest();

    if (this.data.mode === 'create-request') {
      this.dialogRef.close(request);
      return;
    }

    this.loading.set(true);
    const save$: Observable<Prescription> = this.isEdit()
      ? this.prescriptionsService.updateManagedPrescription(this.data.memberId, request as PrescriptionUpdateRequest)
      : this.prescriptionsService.createManagedPrescription(this.data.memberId, request);

    save$.pipe(take(1)).subscribe({
      next: result => {
        this.loading.set(false);
        this.dialogRef.close(result ?? this.data.prescription);
      },
      error: error => {
        console.error('Prescription save failed', error);
        this.loading.set(false);
      },
    });
  }

  private buildRequest(): PrescriptionCreateRequest {
    const value = this.form.getRawValue();
    const endDate = new Date(value.isFreeForm ? value.startDate : value.endDate);
    endDate.setHours(23, 59, 59, 999);

    const metadata = this.buildMetadata(value);

    const base: PrescriptionCreateRequest = {
      type: value.type,
      name: value.name,
      guaranteeStatus: value.guaranteeStatus as GuaranteeStatus,
      startDate: value.startDate.toISOString(),
      endDate: endDate.toISOString(),
      metadata,
      reason: combineReasons({ editReason: '', statusReason: value.statusReason ?? '' }),
      guaranteeId: this.data.prescription?.guaranteeApplicationId ?? this.data.guaranteeApplicationId ?? '',
      franchise: value.franchise,
      considerPaid: value.considerPaid,
      completedInAppointment: value.isFreeForm,
      reservedPrice: value.reservedPrice ?? null,
      categories: value.categories,
      isFreeForm: value.isFreeForm,
    };

    if (this.isEdit()) {
      return { ...base, id: this.data.prescription!.id } as PrescriptionUpdateRequest;
    }
    return base;
  }

  private buildMetadata(value: FreeFormValue): Record<string, string> {
    const metadata: Record<string, string> = { ...((this.data.prescription?.metadata as Record<string, string>) ?? {}) };
    if (value.code) metadata['code'] = value.code;
    if (value.quantity != null) metadata['quantity'] = String(value.quantity);

    return metadata;
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
